import React, { useState, useCallback, useEffect } from 'react';
import {
  Check, X, Loader2, Search, Pencil, Trash2,
  BookOpen, BookMarked, GraduationCap, Layers, Library,
  AlertCircle, CheckCircle2,
} from 'lucide-react';
import type { Subject, Teacher, SubjectTemplate } from '@/actions/data-fetch-curriculum';
import { getCurriculumTemplates, applyTemplate } from '@/actions/data-fetch-curriculum';
import { toastMessage } from '@/utils/toast-message';
import { CARD_CATS, CATEGORY_COLORS } from './curriculum-types';

// ─── Card style helper ────────────────────────────────────────────────────────
const CARD_ICONS = [BookOpen, BookMarked, GraduationCap, Layers, Library];
export function cardStyle(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffff;
  return { cat: CARD_CATS[h % CARD_CATS.length], Icon: CARD_ICONS[h % CARD_ICONS.length] };
}
export { CARD_CATS };

// ─── Side Drawer ──────────────────────────────────────────────────────────────
export const SideDrawer: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, subtitle, onClose, children, footer }) => (
  <>
    <div className="scrim" onClick={onClose} />
    <div className="drawer">
      <div className="drawer-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button className="icon-btn" onClick={onClose} style={{ flexShrink: 0 }}><X size={16} /></button>
      </div>
      <div className="drawer-body">{children}</div>
      {footer && <div className="drawer-foot">{footer}</div>}
    </div>
  </>
);

// ─── Teacher Picker List ──────────────────────────────────────────────────────
export const TeacherPickerList: React.FC<{
  teachers: Teacher[];
  currentId: number | null;
  excludeId?: number | null;
  saving: boolean;
  onSelect: (id: number | null) => void;
}> = ({ teachers, currentId, excludeId, saving, onSelect }) => {
  const [search, setSearch] = useState('');
  const available = teachers.filter(t =>
    t.id !== excludeId && t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search teachers…"
          className="input"
          style={{ paddingLeft: 36, width: '100%' }}
        />
      </div>
      {currentId && (
        <button onClick={() => onSelect(null)} disabled={saving}
          style={{ padding: '8px 12px', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', background: 'transparent', color: 'var(--danger)', fontSize: 12, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
          Remove assignment
        </button>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {available.length === 0
          ? <p className="meta" style={{ padding: '16px 0', textAlign: 'center' }}>No teachers found</p>
          : available.map(t => {
            const isCurrent = t.id === currentId;
            return (
              <button key={t.id} onClick={() => onSelect(t.id)} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', background: isCurrent ? 'var(--accent-wash)' : 'transparent', transition: 'background .1s' }}
                onMouseEnter={e => { if (!isCurrent) (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'; }}
                onMouseLeave={e => { if (!isCurrent) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                {(t as any).photo_url
                  ? <img src={(t as any).photo_url} alt={t.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div className="avatar cat-blue" style={{ width: 32, height: 32, fontSize: 11, flexShrink: 0 }}>
                      {t.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                }
                <span style={{ fontSize: 13.5, flex: 1 }}>{t.name}</span>
                {isCurrent && (saving
                  ? <Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} />
                  : <Check size={14} style={{ color: 'var(--success)' }} />)}
              </button>
            );
          })
        }
      </div>
    </div>
  );
};

// ─── Subject Picker Panel ─────────────────────────────────────────────────────
export const SubjectPickerPanel: React.FC<{
  subjectMaster: Subject[];
  assignedSubjectIds: number[];
  onAssignMultiple: (ids: number[]) => Promise<void>;
  onClose: () => void;
}> = ({ subjectMaster, assignedSubjectIds, onAssignMultiple, onClose }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const unassigned = subjectMaster.filter(
    s => !assignedSubjectIds.includes(s.id) && s.name.toLowerCase().includes(search.toLowerCase())
  );
  const toggle = (id: number) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleAssign = async () => {
    if (!selected.length || saving) return;
    setSaving(true);
    try { await onAssignMultiple(selected); onClose(); } finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search subjects…"
          className="input"
          style={{ paddingLeft: 36, width: '100%' }}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 260, overflowY: 'auto' }}>
        {unassigned.length === 0 && (
          <div style={{ padding: '16px 0', textAlign: 'center' }}>
            <p className="meta">{search ? 'No match in library' : 'All subjects assigned'}</p>
          </div>
        )}
        {unassigned.map(s => {
          const checked = selected.includes(s.id);
          return (
            <button key={s.id} onClick={() => toggle(s.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', background: checked ? 'var(--accent-wash)' : 'transparent', transition: 'background .1s' }}
              onMouseEnter={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'; }}
              onMouseLeave={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? 'var(--accent-deep)' : 'var(--border-2)'}`, background: checked ? 'var(--accent-deep)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {checked && <Check size={10} style={{ color: '#fff' }} />}
              </div>
              <span style={{ flex: 1, fontSize: 13.5 }}>{s.name}</span>
              {s.code && <span className="meta" style={{ fontFamily: 'monospace' }}>{s.code}</span>}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <button onClick={handleAssign} disabled={saving} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          {saving
            ? <><Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} /> Assigning…</>
            : <>Assign {selected.length} subject{selected.length > 1 ? 's' : ''}</>}
        </button>
      )}
      <p className="meta" style={{ textAlign: 'center', paddingTop: 8, borderTop: '1px solid var(--divider)' }}>
        Subject not listed? Go to Subject Library tab to add more.
      </p>
    </div>
  );
};

// ─── Setup Status Badge ───────────────────────────────────────────────────────
export const SetupStatus: React.FC<{ cls: any }> = ({ cls }) => {
  const subjects = cls.PaSubjectMaps?.length ?? 0;
  const sections = cls.PaClassSections ?? [];
  const allSectionsHaveTeacher = sections.length > 0 && sections.every((s: any) => s.primary_teacher_id);
  const allSubjectsHaveTeachers = sections.length > 0 && sections.every((sec: any) =>
    subjects > 0 && (sec.PaSectionSubjectMaps?.length ?? 0) >= subjects &&
    sec.PaSectionSubjectMaps?.every((m: any) => m.teacher_id_fk)
  );
  if (subjects === 0) return (
    <span className="badge b-warning"><AlertCircle size={11} /> No subjects</span>
  );
  if (!allSectionsHaveTeacher || (sections.length > 0 && !allSubjectsHaveTeachers)) return (
    <span className="badge b-warning"><AlertCircle size={11} /> Teachers pending</span>
  );
  return <span className="badge b-success"><CheckCircle2 size={11} /> Setup complete</span>;
};

// ─── Class Card ───────────────────────────────────────────────────────────────
export const ClassCard: React.FC<{
  cls: any;
  onClick: () => void;
  onDelete: () => void;
  onRename: (n: string) => void;
}> = ({ cls, onClick, onDelete, onRename }) => {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [hovered, setHovered] = useState(false);
  const subjects = cls.PaSubjectMaps?.length ?? 0;
  const sections = cls.PaClassSections?.length ?? 0;
  const { cat, Icon } = cardStyle(cls.name);

  return (
    <div
      onClick={renaming ? undefined : onClick}
      className="card card-pad"
      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0, transition: 'box-shadow .15s, border-color .15s', boxShadow: hovered ? 'var(--sh-md)' : 'var(--sh-sm)', borderColor: hovered ? 'var(--border-2)' : 'var(--border)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div className={cat} style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={18} strokeWidth={2} />
        </div>
        <div style={{ display: 'flex', gap: 2, opacity: hovered ? 1 : 0, transition: 'opacity .12s' }} onClick={e => e.stopPropagation()}>
          <button className="icon-btn" style={{ width: 28, height: 28, borderRadius: 7 }}
            onClick={e => { e.stopPropagation(); setRenameVal(cls.name); setRenaming(true); }}>
            <Pencil size={13} />
          </button>
          <button className="icon-btn" style={{ width: 28, height: 28, borderRadius: 7, color: 'var(--danger)' }}
            onClick={e => { e.stopPropagation(); onDelete(); }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {renaming ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }} onClick={e => e.stopPropagation()}>
          <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Enter') { if (renameVal.trim() && renameVal.trim() !== cls.name) onRename(renameVal.trim()); setRenaming(false); }
              if (e.key === 'Escape') setRenaming(false);
            }}
            className="input" style={{ flex: 1, height: 32, fontSize: 13, fontWeight: 700 }}
          />
          <button className="icon-btn" style={{ color: 'var(--success)', width: 28, height: 28 }}
            onClick={() => { if (renameVal.trim() && renameVal.trim() !== cls.name) onRename(renameVal.trim()); setRenaming(false); }}>
            <Check size={13} />
          </button>
          <button className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => setRenaming(false)}><X size={13} /></button>
        </div>
      ) : (
        <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.3px', color: 'var(--text)', marginBottom: 3 }}>{cls.name}</div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <span className="meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <BookMarked size={12} /> {subjects} {subjects === 1 ? 'Subject' : 'Subjects'}
        </span>
        <span className="meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Layers size={12} /> {sections > 0 ? `${sections} ${sections === 1 ? 'Section' : 'Sections'}` : '—'}
        </span>
      </div>

      <div style={{ paddingTop: 12, borderTop: '1px solid var(--divider)' }}>
        <SetupStatus cls={cls} />
      </div>
    </div>
  );
};

// ─── Template Picker Panel ────────────────────────────────────────────────────
export const TemplatePickerPanel: React.FC<{
  boardId: number;
  gradeLabel: string;
  classId: number;
  onApplied: () => Promise<void>;
  onClose: () => void;
}> = ({ boardId, gradeLabel, classId, onApplied, onClose }) => {
  const [stream, setStream] = useState<string | null>(null);
  const [templates, setTemplates] = useState<SubjectTemplate[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const needsStream = ['11', '12'].includes(gradeLabel);

  const loadTemplates = useCallback(async (s?: string) => {
    setLoading(true);
    try {
      const res = await getCurriculumTemplates(boardId, gradeLabel, s);
      const tpls = res?.templates ?? [];
      setTemplates(tpls);
      setSelected(tpls.filter((t: any) => !t.is_optional).map((t: any) => t.subject_name));
    } catch { /* non-critical */ } finally { setLoading(false); }
  }, [boardId, gradeLabel]);

  useEffect(() => {
    if (needsStream && !stream) return;
    loadTemplates(stream ?? undefined);
  }, [stream, needsStream, loadTemplates]);

  const toggle = (name: string) => setSelected(p => p.includes(name) ? p.filter(x => x !== name) : [...p, name]);

  const handleApply = async () => {
    if (!selected.length || applying) return;
    setApplying(true);
    try {
      const res = await applyTemplate(classId, { stream: stream ?? undefined, subject_names: selected });
      if (res?.success) {
        toastMessage(`${res.data?.created ?? 0} subject(s) added from template`, 'success');
        await onApplied();
        onClose();
      } else {
        toastMessage((res as any)?.message || 'Failed to apply template', 'error');
      }
    } finally { setApplying(false); }
  };

  if (needsStream && !stream) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p className="meta" style={{ marginBottom: 6 }}>Select stream for Class {gradeLabel}:</p>
        {['SCIENCE', 'COMMERCE', 'HUMANITIES'].map(s => (
          <button key={s} onClick={() => setStream(s)}
            style={{ padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 'var(--r)', textAlign: 'left', background: 'transparent', cursor: 'pointer', transition: 'all .12s', width: '100%' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-deep)'; (e.currentTarget as HTMLElement).style.background = 'var(--accent-wash)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{s}</p>
          </button>
        ))}
      </div>
    );
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
      <Loader2 size={20} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {needsStream && stream && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="meta">Stream:</span>
          <span className="chip chip-sm cat-blue">{stream}</span>
          <button onClick={() => setStream(null)} className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', fontSize: 12 }}>Change</button>
        </div>
      )}
      {templates.length === 0 ? (
        <p className="meta" style={{ textAlign: 'center', padding: '24px 0' }}>No template found for this grade.</p>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className="meta">{selected.length} of {templates.length} selected</p>
            <button onClick={() => setSelected(templates.map((t: any) => t.subject_name))} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>Select all</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 280, overflowY: 'auto' }}>
            {templates.map((t: any) => {
              const checked = selected.includes(t.subject_name);
              return (
                <button key={t.id} onClick={() => toggle(t.subject_name)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', background: checked ? 'var(--accent-wash)' : 'transparent', transition: 'background .1s' }}
                  onMouseEnter={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'; }}
                  onMouseLeave={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? 'var(--accent-deep)' : 'var(--border-2)'}`, background: checked ? 'var(--accent-deep)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {checked && <Check size={10} style={{ color: '#fff' }} />}
                  </div>
                  <span style={{ flex: 1, fontSize: 13.5 }}>{t.subject_name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {t.category && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text-2)' }} className={CATEGORY_COLORS[t.category] ?? ''}>
                        {t.category}
                      </span>
                    )}
                    {t.is_optional && <span className="meta" style={{ fontSize: 10 }}>opt</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <button onClick={handleApply} disabled={!selected.length || applying} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
            {applying
              ? <><Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} /> Applying…</>
              : <>Apply {selected.length} Subject{selected.length !== 1 ? 's' : ''}</>}
          </button>
        </>
      )}
    </div>
  );
};
